import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { supabase } from '../../lib/supabase';
import { format } from 'date-fns';

function DeviceList() {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, device: null });

  useEffect(() => {
    loadDevices();

    // Subscribe to real-time device updates
    const subscription = supabase
      .channel('devices_list_changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'devices'
      }, () => {
        loadDevices();
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const loadDevices = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;

      const { data, error } = await supabase
        .from('devices')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setDevices(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading devices:', error);
      setLoading(false);
    }
  };

  const handleDeleteDevice = async () => {
    try {
      const { error } = await supabase
        .from('devices')
        .delete()
        .eq('id', deleteDialog.device.id);

      if (error) throw error;

      setDeleteDialog({ open: false, device: null });
      loadDevices();
    } catch (error) {
      console.error('Error deleting device:', error);
      alert('Failed to delete device');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'success';
      case 'offline':
        return 'default';
      default:
        return 'warning';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Devices
        </Typography>
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadDevices}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => alert('Install the mobile app to add a new device')}
          >
            Add Device
          </Button>
        </Box>
      </Box>

      {devices.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No devices registered yet
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            Install the Family Safety Monitor app on Android or iOS devices to get started.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />}>
            View Installation Guide
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Device Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>OS Version</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Seen</TableCell>
                <TableCell>Registered</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.map((device) => (
                <TableRow key={device.id} hover>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {device.device_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={device.device_type} 
                      size="small"
                      color={device.device_type === 'android' ? 'success' : 'info'}
                    />
                  </TableCell>
                  <TableCell>{device.os_version || 'Unknown'}</TableCell>
                  <TableCell>
                    <Chip
                      label={device.status}
                      color={getStatusColor(device.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {device.last_seen 
                      ? format(new Date(device.last_seen), 'MMM dd, yyyy HH:mm')
                      : 'Never'}
                  </TableCell>
                  <TableCell>
                    {format(new Date(device.created_at), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => navigate(`/devices/${device.id}`)}
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteDialog({ open: true, device })}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, device: null })}
      >
        <DialogTitle>Delete Device</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.device?.device_name}"?
            This will also delete all associated data including location history, calls, and recordings.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, device: null })}>
            Cancel
          </Button>
          <Button onClick={handleDeleteDevice} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DeviceList;
